// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useState } from 'react';
import moment from 'moment-timezone';
import { Box, useTheme } from '@mui/system';
import { Typography, Divider, Button, Fade, CircularProgress, TextField, RadioGroup, FormControlLabel, Radio, InputAdornment } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { RootState } from 'Store';
import { GetOrgThunk, UpdateLicenseFeeThunk } from 'Features/admin/adminThunk';
import { licenseFeeModes, prepaidCadences } from 'Utils/constants';
import { LICENSE_FEE_PERCENT, isValidLicenseFeePercent, licenseFeePercentText } from 'Utils/licenseFee';
import HistoryList from 'Components/HistoryList';
import stylesDef from './styles';

moment.tz.setDefault('America/New_York');

const carriesFee = (mode?: string) => mode === 'added' || mode === 'prepaid';

// The three states in the server's words, with the cadence for a prepaid license and the percent for the modes that
// carry a fee (AUT-6622): "prepaid (annual) 10%", "added 12.5%", "none". A fee-carrying row from before the percent
// existed was at the standard, and says so.
export const describeSetting = (mode?: string, prepaidCadence?: string | null, percent?: number | null) => {
  if (!mode) return 'none';
  const base = mode === 'prepaid' && prepaidCadence ? `${mode} (${prepaidCadence})` : mode;
  return carriesFee(mode) ? `${base} ${licenseFeePercentText(percent)}` : base;
};

const modeOption = (mode?: string) => licenseFeeModes.find((option) => option.value === mode);
const cadenceLabel = (cadence?: string | null) => prepaidCadences.find((option) => option.value === cadence)?.label;

/**
 * The account's one license-fee setting (AUT-6569): what each order's price does about the license fee, and at what
 * percent (AUT-6622 — the standard is 10; an account may carry its own). One setting for AEC and catalog orders alike;
 * set here, with a note, by admins with pricing access. A pay-as-you-go account (no plan commitment, no committed
 * tier) never carries one, so its row is locked at "No license fee".
 */
export const LicenseFee = () => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const dispatch = useAppDispatch();

  const { user } = useAppSelector((state: RootState) => state.auth);
  const { org } = useAppSelector((state: RootState) => state.admin);
  const current = org?.licenseFee || { mode: 'none', prepaidCadence: null };
  const currentPercent = typeof current.percent === 'number' ? current.percent : LICENSE_FEE_PERCENT;
  const history = org?.licenseFeeHistory || [];
  // a commitment is a plan, never a tier of its own (one rate per account): pay-as-you-go is the plan name
  const payAsYouGo = org?.currentPricingPlanInfo?.name === 'no_commit';

  const [editing, setEditing] = useState(false);
  const [mode, setMode] = useState<string>(current.mode);
  const [cadence, setCadence] = useState<string>(current.prepaidCadence || 'annual');
  // the percent as typed, so a half-entered "12." is not rounded away under the admin's fingers
  const [percent, setPercent] = useState<string>(String(currentPercent));
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const enteredPercent = Number(percent);
  const percentApplies = carriesFee(mode);
  const percentValid = !percentApplies || (percent.trim() !== '' && isValidLicenseFeePercent(enteredPercent));
  const unchanged = mode === current.mode
    && (mode !== 'prepaid' || cadence === current.prepaidCadence)
    && (!percentApplies || enteredPercent === currentPercent);
  const canSave = !!note.trim() && !unchanged && percentValid && !saving;

  const openEdit = () => {
    setMode(current.mode);
    setCadence(current.prepaidCadence || 'annual');
    setPercent(String(currentPercent));
    setNote('');
    setError('');
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setError('');
  };

  const save = async () => {
    setSaving(true);
    setError('');
    const result = await dispatch(UpdateLicenseFeeThunk(mode, mode === 'prepaid' ? cadence : null, percentApplies ? enteredPercent : null, note.trim()));
    if (result.success) {
      await dispatch(GetOrgThunk(org._id));
      setEditing(false);
    } else {
      setError(result.message || 'Failed to update the license fee');
    }
    setSaving(false);
  };

  const currentOption = modeOption(current.mode);
  // "Added to each order (12.5%)", "Prepaid — not added to orders (Annual) · 12.5%", "No license fee"
  const currentLabel = (() => {
    if (current.mode === 'added') return `${currentOption?.label} (${licenseFeePercentText(currentPercent)})`;
    if (current.mode === 'prepaid') {
      const cadencePart = current.prepaidCadence ? ` (${cadenceLabel(current.prepaidCadence)})` : '';
      return `${currentOption?.label}${cadencePart} · ${licenseFeePercentText(currentPercent)}`;
    }
    return currentOption?.label;
  })();

  return (
    <Box>
      <Typography variant="h4" sx={styles.sectionTitle}>License Fee</Typography>
      <Divider sx={styles.divider} />
      <Box sx={styles.listContent}>
        <Typography>License fee</Typography>
        <Typography>{payAsYouGo ? 'No license fee' : currentLabel}</Typography>
      </Box>
      <Typography variant="body2" sx={styles.helperText}>
        {payAsYouGo ? 'Pay-as-you-go accounts pay list with no license fee.' : currentOption?.description}
      </Typography>
      {!payAsYouGo && !editing && (
        <Box sx={styles.planButtonGroup}>
          <Button disabled={!user?.pricingAccess} sx={styles.planButton} onClick={openEdit} color="primary" variant="outlined">
            Change
          </Button>
        </Box>
      )}
      {editing && (
        <Box sx={styles.form}>
          <RadioGroup aria-label="License fee" value={mode} onChange={(e) => setMode(e.target.value)}>
            {licenseFeeModes.map((option) => (
              <React.Fragment key={option.value}>
                <FormControlLabel value={option.value} control={<Radio />} label={option.label} />
                <Typography variant="body2" sx={styles.optionDescription}>{option.description}</Typography>
                {option.value === 'prepaid' && mode === 'prepaid' && (
                  <RadioGroup row aria-label="Prepaid cadence" value={cadence} onChange={(e) => setCadence(e.target.value)} sx={styles.cadenceGroup}>
                    {prepaidCadences.map((cadenceOption) => (
                      <FormControlLabel key={cadenceOption.value} value={cadenceOption.value} control={<Radio size="small" />} label={cadenceOption.label} />
                    ))}
                  </RadioGroup>
                )}
                {/* the account's percent (AUT-6622), under the option that carries the fee; the standard is 10 */}
                {carriesFee(option.value) && mode === option.value && (
                  <Box sx={styles.percentRow}>
                    <TextField
                      id="licenseFeePercent"
                      label="Percent"
                      variant="standard"
                      type="number"
                      value={percent}
                      onChange={(e) => setPercent(e.target.value)}
                      error={!percentValid}
                      helperText="Above 0, up to 100, two decimals at most"
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment>, inputProps: { step: 0.01, min: 0.01, max: 100 } }}
                      sx={styles.percentField}
                    />
                    {enteredPercent !== LICENSE_FEE_PERCENT && (
                      <Button variant="text" size="small" sx={styles.resetButton} onClick={() => setPercent(String(LICENSE_FEE_PERCENT))}>
                        {`Reset to standard (${licenseFeePercentText(LICENSE_FEE_PERCENT)})`}
                      </Button>
                    )}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </RadioGroup>
          <TextField
            id="licenseFeeNote"
            label="Note (required)"
            variant="standard"
            multiline
            minRows={2}
            onChange={(e) => setNote(e.target.value)}
            value={note}
            sx={styles.notesField}
          />
          {error && <Typography variant="body2" sx={styles.error}>{error}</Typography>}
          <Box sx={styles.buttonGroup}>
            <Button sx={styles.planButton} disabled={!canSave} onClick={save} color="primary" variant="contained">
              <Fade in={saving}>
                <CircularProgress size={24} sx={styles.buttonProgress} />
              </Fade>
              <Fade in={!saving}>
                <span>Save</span>
              </Fade>
            </Button>
            <Button sx={styles.planButton} onClick={cancelEdit} variant="text">Cancel</Button>
          </Box>
        </Box>
      )}
      <HistoryList
        entries={history}
        getKey={(entry) => entry._id}
        renderRow={(entry) => (
          <Box sx={styles.historyRow}>
            <Typography variant="body2">{moment.utc(entry.createdAt).format('MM/DD/YYYY')}</Typography>
            <Typography variant="body2" sx={styles.historyChange}>
              {`${describeSetting(entry.previousMode, entry.previousCadence, entry.previousPercent)} → ${describeSetting(entry.mode, entry.prepaidCadence, entry.percent)}`}
            </Typography>
            <Typography variant="body2">{entry.changedBy}</Typography>
            <Typography variant="body2" sx={styles.historyNotes}>{entry.note}</Typography>
          </Box>
        )}
      />
    </Box>
  );
};

export default LicenseFee;
